import { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/authHooks';
import { getAccessToken } from '@/auth/authStore';
import { bootRefresh } from '@/auth/silentRefresh';
import { MeProvider } from '@/session/MeProvider';
import { HiddenPageTitle } from '@/shell/PageHeader';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { rememberReferral } from './referralStore';
import { ReferralSignedIn } from './ReferralSignedIn';

/**
 * Public `/ref/:code` (outside AuthGate). Remembers the code, then `/` when unsigned or the
 * signed-in referral dispatch (MeProvider) when a token is already in memory.
 */
export function ReferralLanding() {
  const { t } = useTranslation();
  const { code } = useParams();
  const { setAuthed } = useAuth();
  const [gate, setGate] = useState<'booting' | 'anon' | 'authed'>(() =>
    getAccessToken() ? 'authed' : 'booting',
  );
  const started = useRef(false);

  useEffect(() => {
    rememberReferral(code);
  }, [code]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (getAccessToken()) return;
    void bootRefresh().then((ok) => {
      if (ok) {
        setAuthed();
        setGate('authed');
      } else {
        setGate('anon');
      }
    });
  }, [setAuthed]);

  if (gate === 'booting') {
    return (
      <StandaloneFrame>
        <HiddenPageTitle title={t('referral.title')} />
        <Typography variant="body1" role="status">
          {t('common.loading')}
        </Typography>
      </StandaloneFrame>
    );
  }
  if (gate === 'anon') {
    return <Navigate to="/" replace />;
  }

  return (
    <MeProvider>
      <ReferralSignedIn />
    </MeProvider>
  );
}
